import {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Model, Serializer } from "survey-core";
import "survey-core/survey-core.css";
import { Survey } from "survey-react-ui";
import { useQuery, useMutation } from "@apollo/client";
import { Paper, LoadingOverlay, Alert } from "@mantine/core";
import { gql } from "@/__generated__";
import { useSurveySchemaByCondition } from "@/hooks/useSurveySchemaByCondition";
import { appealsHubSurveyTheme } from "./surveyTheme";
import styles from "./survey.module.scss";
import { registerTipQuestion } from "../../../../../../survey/models/tip";
import { registerSuggestionsQuestion } from "../../../../../../survey/models/suggestions";
import { registerSampleStoryQuestion } from "../../../../../../survey/models/sample-story";
import { registerPillMultiSelectButtonQuestion } from "../../../../../../survey/models/pill-multi-select-button";
import {
  filterSurveySchemaByTag,
  SurveySchema,
} from "@/utils/filterSurveySchema";
import { useFormContext } from "@/contexts/FormContext";

const GET_SURVEY_DATA_FOR_APPEAL = gql(`
  query GetSurveyDataForAppeal($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      surveyJsAnswers
      denial {
        condition
        medicationBrandName
        denialReason
      }
    }
  }
`);

const UPDATE_SURVEY_ANSWERS = gql(`
  mutation UpdateSurveyAnswers($appealId: ID!, $surveyJsAnswers: String!, $surveySchemaId: ID!) {
    updateSurveyJsAnswers(
      appealId: $appealId
      surveyJsAnswers: $surveyJsAnswers
      surveySchemaId: $surveySchemaId
    )
  }
`);

registerTipQuestion();
registerSuggestionsQuestion();
registerSampleStoryQuestion();
registerPillMultiSelectButtonQuestion();

export type FilteredSurveyTabHandle = {
  save: () => Promise<boolean>;
  getSurvey: () => Model | null;
  getOriginalData: () => string | null;
};

interface FilteredSurveyTabProps {
  appealId: string;
  readOnly: boolean;
  filterTag?: string;
}

export const FilteredSurveyTab = forwardRef<
  FilteredSurveyTabHandle,
  FilteredSurveyTabProps
>(function FilteredSurveyTab({ appealId, readOnly, filterTag }, ref) {
  const [survey, setSurvey] = useState<Model | null>(null);
  const [originalData, setOriginalData] = useState<string | null>(null);
  const { setIsDirty } = useFormContext();

  const { data, loading, error } = useQuery(GET_SURVEY_DATA_FOR_APPEAL, {
    variables: { appealId },
    fetchPolicy: "no-cache",
  });

  const [updateSurveyJsAnswers] = useMutation(UPDATE_SURVEY_ANSWERS);

  const condition = data?.getAppealById?.denial?.condition || "";

  const {
    surveySchema,
    surveySchemaId,
    loading: schemaLoading,
    error: schemaError,
  } = useSurveySchemaByCondition(condition);

  const filteredSchema = useMemo(() => {
    if (!surveySchema) return null;
    if (!filterTag) return surveySchema as SurveySchema;
    return filterSurveySchemaByTag(surveySchema as SurveySchema, filterTag);
  }, [surveySchema, filterTag]);

  const schemaWithOptionalQuestions = useMemo(() => {
    if (!filteredSchema) return null;

    return {
      ...filteredSchema,
      pages: filteredSchema.pages?.map((page) => ({
        ...page,
        elements: page.elements?.map((element) => ({
          ...element,
          isRequired: false,
        })),
      })),
    };
  }, [filteredSchema]);

  useEffect(() => {
    Serializer.addProperty("boolean", {
      name: "renderAs",
      default: "radio",
    });
  }, []);

  useEffect(() => {
    if (data?.getAppealById && schemaWithOptionalQuestions && surveySchemaId) {
      const contextData = {
        "context.condition": condition,
        "context.medication": data.getAppealById.denial?.medicationBrandName,
        "context.denialReason": data.getAppealById.denial?.denialReason,
      };

      let initialData: Record<string, unknown> = contextData;

      if (data.getAppealById.surveyJsAnswers) {
        try {
          const existingAnswers = JSON.parse(
            data.getAppealById.surveyJsAnswers,
          );
          initialData = { ...contextData, ...existingAnswers };
        } catch (parseError) {
          console.error("Error parsing existing survey answers:", parseError);
        }
      }

      const newSurvey = new Model({
        ...schemaWithOptionalQuestions,
        showNavigationButtons: "none",
        clearInvisibleValues: "onHiddenContainer",
        requiredText: "",
        questionsOnPageMode: "singlePage",
      });

      newSurvey.data = initialData;
      newSurvey.showCompletedPage = false;
      newSurvey.applyTheme(appealsHubSurveyTheme);

      setOriginalData(JSON.stringify(initialData));
      newSurvey.css = {
        body: styles.body,
        page: {
          title: styles.pageTitle,
          description: styles.pageDescription,
        },
        checkbox: {
          root: styles.checkboxPillRoot,
          materialDecorator: styles.checkboxMaterialDecorator,
          itemChecked: styles.checkboxItemChecked,
        },
        actionBar: {
          root: styles.actionBarRoot,
        },
        comment: {
          root: styles.commentRoot,
        },
        boolean: {
          rootRadio: styles.booleanPillRoot,
          radioFieldset: styles.booleanPillRoot,
          radioItem: "sd-item sd-radio sd-selectbase__item",
          radioItemChecked: styles.booleanRadioItemChecked,
          radioLabel: "sd-selectbase__label",
          materialRadioDecorator: "sd-item__decorator sd-radio__decorator",
        },
        dropdown: {
          control: styles.dropdownControl,
          controlValue: styles.dropdownControlValue,
          filterStringInput: styles.dropdownFilterStringInput,
          other: styles.dropdownOther,
        },
        list: {
          root: styles.listRoot,
          itemSelected: styles.listItemSelected,
          itemFocused: styles.listItemFocused,
          item: styles.listItem,
          itemBody: styles.listItemBody,
        },
        radiogroup: {
          root: styles.radiogroupPillRoot,
          itemChecked: styles.radiogroupItemChecked,
        },
        text: {
          root: styles.textRoot,
        },
        tagbox: {
          selectWrapper: styles.tagboxSelectWrapper,
          control: styles.tagboxControl,
          filterStringInput: styles.tagboxFilterStringInput,
          controlValue: styles.tagboxControlValue,
          cleanItemButton: styles.tagboxCleanItemButton,
          cleanItemButtonSvg: styles.tagboxCleanItemButtonSvg,
          hintSuffix: styles.tagboxHintSuffix,
          popup: styles.tagboxPopup,
        },
        paneldynamic: {
          mainRoot: styles.paneldynamicMainRoot,
          buttonAdd: styles.paneldynamicButtonAdd,
          buttonRemove: styles.paneldynamicButtonRemove,
        },
      };

      if (readOnly) {
        newSurvey.mode = "display";
      } else {
        newSurvey.mode = "edit";
      }

      setSurvey(newSurvey);
    }
  }, [
    data,
    schemaWithOptionalQuestions,
    surveySchemaId,
    appealId,
    condition,
    readOnly,
    updateSurveyJsAnswers,
  ]);

  const handleSave = async (): Promise<boolean> => {
    if (!survey || !surveySchemaId) return false;

    const surveyJsAnswers = JSON.stringify(survey.data);
    try {
      await updateSurveyJsAnswers({
        variables: {
          appealId,
          surveyJsAnswers,
          surveySchemaId,
        },
      });
      setOriginalData(surveyJsAnswers);
      return true;
    } catch (saveError) {
      console.error("Error saving survey answers:", saveError);
      return false;
    }
  };

  useEffect(() => {
    if (!survey || readOnly || !originalData) return;

    const valueChangedHandler = () => {
      const currentData = JSON.stringify(survey.data);
      const isDirty = currentData !== originalData;
      setIsDirty(isDirty);
    };

    survey.onValueChanged.add(valueChangedHandler);
  }, [survey, originalData, readOnly, setIsDirty]);

  useEffect(() => {
    return () => {
      setIsDirty(false);
    };
  }, [setIsDirty]);

  useImperativeHandle(ref, () => ({
    save: handleSave,
    getSurvey: () => survey,
    getOriginalData: () => originalData,
  }));

  if (loading || schemaLoading) {
    return (
      <Paper p="xl" shadow="sm" withBorder>
        <LoadingOverlay visible />
        <div style={{ minHeight: "200px" }} />
      </Paper>
    );
  }

  if (error || schemaError) {
    return (
      <Paper p="xl" shadow="sm" withBorder>
        <Alert color="red" title="Error loading survey">
          {schemaError
            ? "No survey found for this condition. Please ensure the condition is properly configured."
            : "Unable to load survey data. Please try again."}
        </Alert>
      </Paper>
    );
  }

  if (
    !filteredSchema ||
    (filteredSchema.pages && filteredSchema.pages.length === 0)
  ) {
    return (
      <Paper p="xl" shadow="sm" withBorder>
        <Alert color="blue" title="No questions available">
          No questions are currently available for this category.
        </Alert>
      </Paper>
    );
  }

  if (!survey) {
    return (
      <Paper p="xl" shadow="sm" withBorder>
        <Alert color="blue" title="No survey available">
          No patient survey is currently available for this appeal.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper className={styles.container} p="xl" shadow="sm" withBorder>
      <Survey model={survey} />
    </Paper>
  );
});
